import React, { useMemo, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Card,
  IconButton,
  Menu,
  MenuItem,
  Tab,
  Tabs,
  TextField,
  Typography,
  CircularProgress,
  Alert,
  Snackbar,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import EditIcon from '@mui/icons-material/Edit';
import SearchIcon from '@mui/icons-material/Search';
import BusinessIcon from '@mui/icons-material/Business';
import { getEntities } from '../../../../api/entityApi';
import { useErrorModal } from '../../../../hooks/useErrorModal';
import PermissionGuard from '../../../../components/PermissionGuard';

const filterInputStyle = {
  borderRadius: '10px',
  backgroundColor: '#fff',
  transition: 'all 0.2s ease-in-out',
  '& .MuiOutlinedInput-notchedOutline': {
    borderColor: 'rgba(226, 232, 240, 0.8)',
  },
  '&:hover .MuiOutlinedInput-notchedOutline': {
    borderColor: 'rgba(117, 34, 209, 0.4)',
  },
  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
    borderColor: '#7522D1',
  },
};

const LegalEntities = () => {
  const navigate = useNavigate();
  const [entities, setEntities] = useState([]);
  const [selectedEntityId, setSelectedEntityId] = useState(null);
  const [tab, setTab] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [menuAnchorEl, setMenuAnchorEl] = useState(null);
  const { showError, ErrorModal } = useErrorModal();

  const filteredEntities = useMemo(() => {
    return entities.filter((e) => e.name.toLowerCase().includes(searchQuery.toLowerCase()));
  }, [entities, searchQuery]);

  const selectedEntity = useMemo(() => {
    return entities.find((e) => e.id === selectedEntityId);
  }, [entities, selectedEntityId]);

  const handleAddClick = () => {
    navigate('/org-management/org-structure/add-legal-entity');
  };

  const handleMenuClick = (event, entity) => {
    event.stopPropagation();
    setMenuAnchorEl(event.currentTarget);
    setSelectedEntityId(entity.id);
  };

  const handleMenuClose = () => {
    setMenuAnchorEl(null);
  };

  const handleEdit = () => {
    if (selectedEntity) {
      navigate(`/org-management/org-structure/edit-legal-entity/${selectedEntity.id}`, {
        state: { entityData: selectedEntity }
      });
    }
    handleMenuClose();
  };

  const fetchEntities = async () => {
    try {
      setLoading(true);
      const result = await getEntities();
      if (result.success) {
        setEntities(result.data);
        if (result.data.length > 0 && !selectedEntityId) {
          setSelectedEntityId(result.data[0].id);
        }
      } else {
        setError(result.message);
      }
    } catch (error) {
      showError(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchEntities();
  }, []);

  const handleSnackbarClose = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  return (
    <Box sx={{ width: '100%', marginTop: 0 }}>
      <Box
        sx={{
          backgroundColor: '#FFFFFF',
          padding: '20px 24px',
          marginBottom: 3,
          borderRadius: '12px',
          border: '1px solid rgba(117, 34, 209, 0.08)',
          borderLeft: '6px solid #7522D1',
          boxShadow: '0 4px 20px rgba(117, 34, 209, 0.04)',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <Box>
          <Typography
            variant="h6"
            sx={{
              fontWeight: 700,
              color: '#030007',
              marginBottom: 0.5,
              fontSize: '1.1rem',
            }}
          >
            Legal Entities
          </Typography>
          <Typography
            variant="body2"
            sx={{
              color: '#4F4937',
              fontSize: '0.85rem',
            }}
          >
            Manage your organization's legal entities
          </Typography>
        </Box>
        <PermissionGuard module="entities" action="create">
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleAddClick}
            sx={{
              backgroundColor: '#7522D1',
              color: '#FFFFFF',
              textTransform: 'none',
              fontWeight: 600,
              paddingX: 2.5,
              paddingY: 1.2,
              borderRadius: '10px',
              whiteSpace: 'nowrap',
              boxShadow: '0 4px 12px rgba(117, 34, 209, 0.2)',
              transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
              '&:hover': {
                backgroundColor: '#5E1BA8',
                boxShadow: '0 6px 16px rgba(117, 34, 209, 0.3)',
                transform: 'translateY(-1px)',
              },
            }}
          >
            Add Legal Entity
          </Button>
        </PermissionGuard>
      </Box>

      <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 3 }}>
        {/* Sidebar */}
        <Box sx={{ width: { xs: '100%', md: '30%' }, minWidth: { xs: '100%', md: 300 } }}>
          <TextField
            fullWidth
            placeholder="Search here"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            InputProps={{
              startAdornment: <SearchIcon sx={{ marginRight: 1, color: '#7522D1', fontSize: '1.2rem' }} />,
            }}
            variant="outlined"
            sx={{
              marginBottom: 2,
              '& .MuiOutlinedInput-root': filterInputStyle,
            }}
          />

          <Typography
            sx={{
              fontWeight: 600,
              color: '#030007',
              marginBottom: 2,
              fontSize: '0.9rem',
            }}
          >
            Legal Entities ({filteredEntities.length})
          </Typography>

          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
              <CircularProgress />
            </Box>
          ) : error ? (
            <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>
          ) : (
            filteredEntities.map((entity) => (
              <Card
                key={entity.id}
                onClick={() => setSelectedEntityId(entity.id)}
                sx={{
                  padding: 2,
                  marginBottom: 1.5,
                  cursor: 'pointer',
                  backgroundColor: selectedEntityId === entity.id ? '#F5EEFC' : '#FFFFFF',
                  border: selectedEntityId === entity.id ? '1px solid #7522D1' : '1px solid rgba(117, 34, 209, 0.08)',
                  borderLeft: selectedEntityId === entity.id ? '6px solid #7522D1' : '1px solid rgba(117, 34, 209, 0.08)',
                  borderRadius: '12px',
                  transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                  boxShadow: selectedEntityId === entity.id ? '0 4px 12px rgba(117, 34, 209, 0.08)' : 'none',
                  '&:hover': {
                    transform: 'translateY(-2px)',
                    boxShadow: '0 6px 20px rgba(117, 34, 209, 0.12)',
                    borderColor: '#7522D1',
                  },
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Box
                    sx={{
                      width: 40,
                      height: 40,
                      borderRadius: '10px',
                      backgroundColor: 'rgba(117, 34, 209, 0.08)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0,
                      transition: 'all 0.2s ease-in-out',
                    }}
                  >
                    <BusinessIcon sx={{ color: '#7522D1', fontSize: '1.2rem' }} />
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <Typography
                      sx={{
                        fontWeight: 600,
                        color: '#030007',
                        fontSize: '0.95rem',
                      }}
                    >
                      {entity.name}
                    </Typography>
                    <Typography
                      sx={{
                        color: '#4F4937',
                        fontSize: '0.85rem',
                      }}
                    >
                      {entity.country || 'No country specified'}
                    </Typography>
                  </Box>
                  <IconButton onClick={(e) => handleMenuClick(e, entity)} size="small">
                    <MoreVertIcon sx={{ color: '#4F4937' }} />
                  </IconButton>
                </Box>
              </Card>
            ))
          )}
        </Box>

        {/* Main Content */}
        <Box sx={{ flex: 1 }}>
          {selectedEntity ? (
            <Card
              sx={{
                padding: 4,
                boxShadow: '0 4px 20px rgba(117, 34, 209, 0.02)',
                border: '1px solid rgba(117, 34, 209, 0.08)',
                borderRadius: '16px',
                backgroundColor: '#FFFFFF',
              }}
            >
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Box
                    sx={{
                      width: 56,
                      height: 56,
                      borderRadius: '12px',
                      backgroundColor: 'rgba(117, 34, 209, 0.08)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      border: '1px solid rgba(117, 34, 209, 0.15)',
                    }}
                  >
                    <BusinessIcon sx={{ color: '#7522D1', fontSize: '1.6rem' }} />
                  </Box>
                  <Box>
                    <Typography
                      sx={{
                        fontWeight: 650,
                        color: '#030007',
                        fontSize: '1.1rem',
                      }}
                    >
                      {selectedEntity.name}
                    </Typography>
                    <Typography
                      sx={{
                        color: '#4F4937',
                        fontSize: '0.9rem',
                      }}
                    >
                      {selectedEntity.legalEntityName || 'No legal entity name'}
                    </Typography>
                  </Box>
                </Box>
              </Box>

              <Tabs
                value={tab}
                onChange={(_, v) => setTab(v)}
                sx={{
                  borderBottom: '1px solid rgba(117, 34, 209, 0.08)',
                  marginBottom: 3,
                  '& .MuiTabs-indicator': {
                    backgroundColor: '#7522D1',
                    height: '3px',
                    borderRadius: '3px 3px 0 0',
                  },
                }}
              >
                <Tab
                  label="Summary"
                  sx={{
                    textTransform: 'none',
                    fontSize: '0.95rem',
                    color: '#4F4937',
                    fontWeight: tab === 0 ? 600 : 400,
                    '&.Mui-selected': {
                      color: '#7522D1',
                    },
                    transition: 'all 0.2s ease-in-out',
                    '&:hover': {
                      color: '#7522D1',
                    },
                  }}
                />
                <Tab
                  label="Details"
                  sx={{
                    textTransform: 'none',
                    fontSize: '0.95rem',
                    color: '#4F4937',
                    fontWeight: tab === 1 ? 600 : 400,
                    '&.Mui-selected': {
                      color: '#7522D1',
                    },
                    transition: 'all 0.2s ease-in-out',
                    '&:hover': {
                      color: '#7522D1',
                    },
                  }}
                />
              </Tabs>

              {tab === 0 && (
                <Box>
                  <Typography
                    sx={{
                      fontWeight: 600,
                      color: '#030007',
                      marginBottom: 1,
                      fontSize: '0.9rem',
                    }}
                  >
                    Description
                  </Typography>
                  <Typography
                    sx={{
                      color: '#4F4937',
                      fontSize: '0.9rem',
                      marginBottom: 3,
                    }}
                  >
                    {selectedEntity.description || 'No description available for this legal entity.'}
                  </Typography>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <Box>
                      <Typography sx={{ fontWeight: 600, color: '#030007', fontSize: '0.9rem' }}>
                        Company ID
                      </Typography>
                      <Typography sx={{ color: '#4F4937', fontSize: '0.9rem' }}>
                        {selectedEntity.companyId || 'N/A'}
                      </Typography>
                    </Box>
                    <Box>
                      <Typography sx={{ fontWeight: 600, color: '#030007', fontSize: '0.9rem' }}>
                        Business Type
                      </Typography>
                      <Typography sx={{ color: '#4F4937', fontSize: '0.9rem' }}>
                        {selectedEntity.businessType || 'N/A'}
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              )}

              {tab === 1 && (
                <Box>
                  <Typography sx={{ fontWeight: 600, color: '#030007', marginBottom: 2, fontSize: '0.9rem' }}>
                    Entity Details
                  </Typography>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <Box>
                      <Typography sx={{ fontWeight: 600, color: '#030007', fontSize: '0.9rem' }}>
                        Country
                      </Typography>
                      <Typography sx={{ color: '#4F4937', fontSize: '0.9rem' }}>
                        {selectedEntity.country || 'N/A'}
                      </Typography>
                    </Box>
                    <Box>
                      <Typography sx={{ fontWeight: 600, color: '#030007', fontSize: '0.9rem' }}>
                        Address
                      </Typography>
                      <Typography sx={{ color: '#4F4937', fontSize: '0.9rem' }}>
                        {selectedEntity.address || 'N/A'}
                      </Typography>
                    </Box>
                    <Box>
                      <Typography sx={{ fontWeight: 600, color: '#030007', fontSize: '0.9rem' }}>
                        Industry
                      </Typography>
                      <Typography sx={{ color: '#4F4937', fontSize: '0.9rem' }}>
                        {selectedEntity.industry || 'N/A'}
                      </Typography>
                    </Box>
                    <Box>
                      <Typography sx={{ fontWeight: 600, color: '#030007', fontSize: '0.9rem' }}>
                        Website
                      </Typography>
                      <Typography sx={{ color: '#4F4937', fontSize: '0.9rem' }}>
                        {selectedEntity.website || 'N/A'}
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              )}
            </Card>
          ) : (
            <Card
              sx={{
                padding: 4,
                boxShadow: '0 4px 20px rgba(117, 34, 209, 0.02)',
                border: '1px solid rgba(117, 34, 209, 0.08)',
                borderRadius: '16px',
                textAlign: 'center',
                backgroundColor: '#FFFFFF',
              }}
            >
              <Typography color="text.secondary">Select a legal entity to view details</Typography>
            </Card>
          )}
        </Box>
      </Box>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity={snackbar.severity}
          sx={{
            width: '100%',
            borderRadius: '12px',
            boxShadow: '0 8px 24px rgba(0, 0, 0, 0.08)',
          }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>

      <Menu
        anchorEl={menuAnchorEl}
        open={Boolean(menuAnchorEl)}
        onClose={handleMenuClose}
        PaperProps={{
          elevation: 0,
          sx: {
            mt: 1.5,
            minWidth: 160,
            borderRadius: '12px',
            border: '1px solid rgba(117, 34, 209, 0.08)',
            boxShadow: '0 10px 30px rgba(117, 34, 209, 0.08)',
            overflow: 'visible',
            '& .MuiMenuItem-root': {
              fontSize: '0.9rem',
              padding: '10px 16px',
              color: '#42374F',
              borderRadius: '8px',
              margin: '4px 8px',
              transition: 'all 0.15s ease-in-out',
              '&:hover': {
                backgroundColor: 'rgba(117, 34, 209, 0.05)',
                color: '#7522D1',
              },
            },
          },
        }}
      >
        <PermissionGuard module="entities" action="edit">
          <MenuItem onClick={handleEdit}>
            <EditIcon sx={{ mr: 1.5, fontSize: 18 }} />
            Edit
          </MenuItem>
        </PermissionGuard>
      </Menu>

      <ErrorModal />
    </Box>
  );
};

export default LegalEntities;
